import axios from 'axios';
import Cookies from 'js-cookie';
import useSWRImmutable from 'swr/immutable';

const IMAGE_CDN_URL = 'https://d207ibygpg2z1x.cloudfront.net/image/upload';

export type User = {
    id: number;
    hash: string;
    email: string;
    role: string;
    logo: string;
    username: string;
    alerts: number;
    additionalRoles: string;
    unsubscribed: boolean;
    profile_credential: string;
    job_title: string;
    isVip: number;
    isClient: boolean;
    isBot: boolean;
    isLoggedIn: boolean;
    isAdmin: boolean;
    baseLogo: string;
    pendingConnections: number;
    unreadMessageCount: number;
    totalCount: number;
    karma_score: number;
    karmaScore: number;
};

export type UserReturn = {
    user: User;
    userLoading: boolean;
    userError: any;
    statusLoading: boolean;
};

const loggedOutUser: User = {
    baseLogo: '',
    isLoggedIn: false,
    isAdmin: false,
    isClient: false,
    isBot: false,
    isVip: 0,
    karma_score: 0,
    pendingConnections: 0,
    unreadMessageCount: 0,
    totalCount: 0,
    additionalRoles: '',
    alerts: 0,
    email: '',
    hash: '',
    id: 0,
    job_title: '',
    karmaScore: 0,
    logo: '',
    profile_credential: '',
    role: 'UNAUTH',
    unsubscribed: false,
    username: '',
};

const fetcher = async (url: string) => axios.get(url).then((res) => res.data);

const checkifIsVip = (badges: { type: string; earned: number }[]) => {
    let isVip: number = 0;
    badges.forEach((badge) => {
        if (badge.type.toLowerCase() === 'vip') {
            isVip = badge.earned;
        }
    });
    return isVip;
};

export default function useUser(): UserReturn {
    try {
        // check for cookie if it does not  exist return loggedOutUser
        if (!Cookies.get('CAKEPHP')) {
            return {
                user: loggedOutUser,
                userLoading: false,
                userError: false,
                statusLoading: false,
            };
        }

        const {
            data: user,
            error,
            isLoading,
        } = useSWRImmutable<User>('/api/user', fetcher, {
            onErrorRetry: (
                errorMessage,
                key,
                config,
                revalidate,
                { retryCount }
            ) => {
                if (errorMessage.status === 404) return;
                if (key === '/api/user') return;
                if (retryCount >= 4) return;
                setTimeout(() => revalidate({ retryCount }), 5000);
            },
        });

        const notificationResponse = useSWRImmutable(
            user?.isLoggedIn ? '/api/community/feed/notifications' : null,
            fetcher,
            {
                onErrorRetry: (
                    errorMessage,
                    key,
                    config,
                    revalidate,
                    { retryCount }
                ) => {
                    if (errorMessage.status === 404) return;
                    if (retryCount >= 4) return;
                    setTimeout(() => revalidate({ retryCount }), 5000);
                },
            }
        );

        const {
            data: status,
            error: statusError,
            isLoading: statusLoading,
        } = useSWRImmutable(
            user?.isLoggedIn ? '/api/user/status' : null,
            fetcher,
            {
                onErrorRetry: (
                    errorMessage,
                    key,
                    config,
                    revalidate,
                    { retryCount }
                ) => {
                    if (errorMessage.status === 404) return;
                    if (retryCount >= 4) return;
                    setTimeout(() => revalidate({ retryCount }), 5000);
                },
            }
        );
        if (!user?.isLoggedIn) {
            return {
                user: loggedOutUser,
                userLoading: isLoading,
                userError: error,
                statusLoading: false,
            };
        }
        const notification = notificationResponse?.data;
        if (user?.isLoggedIn && notification) {
            user.alerts = notification.notificationData.unseenCount;
        }
        user.karmaScore = user.karma_score;
        user.logo = user?.baseLogo && user?.baseLogo !== ''
            ? `${IMAGE_CDN_URL}/${user.baseLogo}`
            : user.baseLogo;

        if (user?.isLoggedIn && status) {
            user.pendingConnections = status.personalized?.pendingConnections;
            user.unreadMessageCount = status.personalized?.unreadMessageCount;
            user.totalCount = user.pendingConnections + user.unreadMessageCount;
            user.isVip = checkifIsVip(
                status.communityOnboarding?.userProfileData?.badges ?? []
            );
        }
        return {
            user,
            userLoading: isLoading,
            statusLoading,
            userError: error || statusError,
        };
    } catch (e) {
        return {
            user: loggedOutUser,
            userLoading: false,
            userError: e,
            statusLoading: false,
        };
    }
}
